<script lang="ts">
  import { fade, fly } from 'svelte/transition'
  import { isCartUpdating } from '../store/store'
  import Money from './Money.svelte'
  import ShopifyImage from './ShopifyImage.svelte'

  type CartLineItem = {
    product_id: number
    handle: string
  }

  type RecommendedVariant = {
    id: number
    title: string
    available: boolean
    price: number
    option1: string | null
    option2: string | null
    option3: string | null
  }

  // recommendations/products.json returns option objects, not bare strings
  type RecommendedOption = {
    name: string
    position: number
    values: string[]
  }

  type RecommendedProduct = {
    id: number
    title: string
    handle: string
    price: number
    featured_image: string | null
    images: string[]
    options: RecommendedOption[]
    variants: RecommendedVariant[]
  }

  export let cartItems: CartLineItem[] = []
  export let onAdd: () => void | Promise<void>

  let products: RecommendedProduct[] = []
  let loading = false
  let addingVariantId: number | null = null
  let loadedKey = ''
  let quickBuyProduct: RecommendedProduct | null = null
  let selectedOptions: Record<string, string> = {}
  let quantity = 1

  $: cartHandles = new Set(cartItems.map((item) => item.handle))
  $: anchorProductId = cartItems[0]?.product_id ?? null
  $: visibleProducts = products.filter((product) => !cartHandles.has(product.handle))

  $: selectableOptions = (quickBuyProduct?.options ?? []).filter((option) => {
    const values = option.values.filter((value) => value && value !== 'Default Title')
    return values.length > 1
  }).sort((a, b) => a.position - b.position)

  $: selectedVariant = quickBuyProduct
    ? quickBuyProduct.variants.find((variant) =>
        selectableOptions.every((option) => {
          const value = variant[`option${option.position}` as keyof RecommendedVariant]
          return value === selectedOptions[option.name]
        }),
      ) ?? getFirstAvailableVariant(quickBuyProduct)
    : null

  $: if (anchorProductId) {
    const key = String(anchorProductId)
    if (key !== loadedKey) {
      loadedKey = key
      closeQuickBuy()
      loadRecommendations(anchorProductId)
    }
  } else {
    products = []
    loadedKey = ''
    closeQuickBuy()
  }

  async function loadRecommendations(productId: number) {
    loading = true

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&intent=complementary&limit=8`,
      )
      if (!response.ok) {
        products = []
        return
      }

      const data = await response.json()
      products = (data.products ?? []).filter(
        (product: RecommendedProduct) => !cartHandles.has(product.handle),
      )
    } catch {
      products = []
    } finally {
      loading = false
    }
  }

  function getFirstAvailableVariant(product: RecommendedProduct) {
    return product.variants.find((variant) => variant.available)
  }

  function hasMultipleVariants(product: RecommendedProduct) {
    return product.variants.filter((variant) => variant.available).length > 1
    return (product.options ?? []).some((option) => {
      const values = option.values.filter((value) => value && value !== 'Default Title')
      return values.length > 1
    })
  }

  function getProductImage(product: RecommendedProduct, size = 160) {
    const imageUrl = product.featured_image || product.images[0]
    if (!imageUrl) return null

    return {
      url: imageUrl,
      width: size,
      height: size,
      altText: product.title,
    }
  }

  function openQuickBuy(product: RecommendedProduct) {
    const variant = getFirstAvailableVariant(product)
    if (!variant) return

    quantity = 1
    selectedOptions = {}
    ;(product.options ?? []).forEach((option) => {
      const value = variant[`option${option.position}` as keyof RecommendedVariant]
      if (typeof value === 'string' && value !== 'Default Title') {
        selectedOptions[option.name] = value
      }
    })
    quickBuyProduct = product
  }

  function closeQuickBuy() {
    quickBuyProduct = null
    selectedOptions = {}
    quantity = 1
  }

  function updateOption(name: string, value: string) {
    selectedOptions = { ...selectedOptions, [name]: value }
  }

  function adjustQuantity(delta: number) {
    quantity = Math.max(1, quantity + delta)
  }

  async function addVariantToCart(variantId: number, qty = 1) {
    addingVariantId = variantId
    $isCartUpdating = true

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty }),
      })

      const result = await response.json()
      if (result.status) {
        $isCartUpdating = false
        return
      }

      closeQuickBuy()
      await onAdd()
    } catch {
      $isCartUpdating = false
    } finally {
      addingVariantId = null
    }
  }

  async function addToCart(product: RecommendedProduct) {
    if (hasMultipleVariants(product)) {
      openQuickBuy(product)
      return
    }

    const variant = getFirstAvailableVariant(product)
    if (!variant) return

    await addVariantToCart(variant.id, 1)
  }

  async function submitQuickBuy() {
    if (!selectedVariant?.available) return
    await addVariantToCart(selectedVariant.id, quantity)
  }
</script>

{#if anchorProductId && (loading || visibleProducts.length > 0)}
  <div class="mt-6 border-t border-gray-200 pt-4">
    <h3 class="mb-3 text-sm font-bold uppercase tracking-wide text-gray-900">
      Pairs well with
    </h3>

    {#if loading && visibleProducts.length === 0}
      <p class="text-sm text-gray-500">Loading recommendations…</p>
    {:else}
      <div class="flex gap-2.5 scroll-container pb-1 px-1 w-full max-md:max-w-[80vw]">
        {#each visibleProducts as product (product.id)}
          {@const image = getProductImage(product)}
          {@const variant = getFirstAvailableVariant(product)}
          {@const multiVariant = hasMultipleVariants(product)}
          <div
            class="flex w-36 scroll-item flex-col overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
          >
            <a href={`/products/${product.handle}`} class="block bg-white p-1.5">
              {#if image}
                <ShopifyImage
                  {image}
                  classList="aspect-square h-28 w-full object-contain mix-blend-multiply"
                  sizes="112px"
                  altText={product.title}
                />
              {/if}
            </a>
            <div class="flex flex-col gap-1.5 p-2">
              <a
                href={`/products/${product.handle}`}
                class="min-h-[2rem] text-xs font-medium leading-tight text-gray-900 line-clamp-2"
              >
                {product.title}
              </a>
              <div class="flex items-center justify-between gap-1.5">
                <span class="text-sm font-bold text-primary">
                  <Money price={String(variant?.price || product.price)} showCurrency={true} />
                </span>
                {#if variant}
                  <button
                    type="button"
                    class="shrink-0 rounded-lg border-2 border-primary px-2 py-1 text-xs font-bold text-primary disabled:opacity-50"
                    disabled={$isCartUpdating || addingVariantId === variant.id}
                    on:click={() => addToCart(product)}
                  >
                    {#if addingVariantId === variant.id}
                      …
                    {:else if multiVariant}
                      Quick buy
                    {:else}
                      Add
                    {/if}
                  </button>
                {/if}
              </div>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  </div>
{/if}

{#if quickBuyProduct}
  {@const image = getProductImage(quickBuyProduct, 320)}
  <div
    class="pointer-events-none fixed inset-y-0 right-0 z-[60] flex w-screen max-w-md items-end sm:items-center sm:justify-center sm:p-6"
    transition:fade={{ duration: 150 }}
  >
    <button
      type="button"
      class="pointer-events-auto absolute inset-0 bg-black/20"
      aria-label="Close quick buy"
      on:click={closeQuickBuy}
    />
    <div
      class="pointer-events-auto relative z-10 w-full rounded-t-2xl border-t border-gray-200 bg-white p-4 shadow-2xl sm:max-w-sm sm:rounded-2xl sm:border sm:p-5 sm:shadow-xl"
      role="dialog"
      aria-modal="true"
      aria-label="Quick buy"
      in:fly={{ y: 40, duration: 200 }}
    >
      <button
        type="button"
        class="absolute right-3 top-3 z-10 rounded-full p-1.5 text-gray-700 hover:bg-gray-100 sm:right-4 sm:top-4"
        aria-label="Close quick buy"
        on:click={closeQuickBuy}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M14.25 1.75L1.75 14.25M1.75 1.75L14.25 14.25"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="flex flex-col gap-3 sm:gap-4">
        <div class="flex items-start gap-3 sm:flex-col sm:items-stretch sm:gap-4">
          {#if image}
            <div
              class="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-white sm:mx-auto sm:h-40 sm:w-40 sm:rounded-xl"
            >
              <ShopifyImage
                {image}
                classList="h-full w-full object-contain mix-blend-multiply"
                sizes="(min-width: 640px) 160px, 56px"
                altText={quickBuyProduct.title}
              />
            </div>
          {/if}
          <div class="min-w-0 flex-1 pr-6 sm:pr-0 sm:text-center">
            <p class="text-sm font-bold leading-tight text-gray-900 sm:text-base">
              {quickBuyProduct.title}
            </p>
            <p class="mt-1 text-sm font-bold text-primary sm:mt-2 sm:text-lg">
              <Money
                price={String(selectedVariant?.price ?? quickBuyProduct.price)}
                showCurrency={true}
              />
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-3 sm:gap-4">
          <div
            class="inline-flex w-fit items-stretch overflow-hidden rounded-lg border border-gray-300 sm:mx-auto"
          >
            <button
              type="button"
              class="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:px-4 sm:py-2"
              disabled={quantity <= 1 || $isCartUpdating}
              aria-label="Decrease quantity"
              on:click={() => adjustQuantity(-1)}
            >
              −
            </button>
            <span
              class="min-w-[2.5rem] border-x border-gray-300 px-2 py-1.5 text-center text-sm font-medium sm:min-w-[3rem] sm:py-2"
            >
              {quantity}
            </span>
            <button
              type="button"
              class="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:px-4 sm:py-2"
              disabled={$isCartUpdating}
              aria-label="Increase quantity"
              on:click={() => adjustQuantity(1)}
            >
              +
            </button>
          </div>

          {#each selectableOptions as option (option.position)}
            <div>
              <label
                class="mb-1 block text-sm font-bold text-gray-900"
                for="qb-{option.position}"
              >
                {option.name}
              </label>
              <select
                id="qb-{option.position}"
                class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm sm:py-2.5"
                value={selectedOptions[option.name]}
                on:change={(e) => updateOption(option.name, e.currentTarget.value)}
              >
                {#each option.values.filter((value) => value !== 'Default Title') as value}
                  <option {value}>{value}</option>
                {/each}
              </select>
            </div>
          {/each}

          <button
            type="button"
            class="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50 sm:py-3 sm:text-base"
            disabled={$isCartUpdating ||
              !selectedVariant?.available ||
              addingVariantId === selectedVariant?.id}
            on:click={submitQuickBuy}
          >
            {#if addingVariantId === selectedVariant?.id}
              Adding…
            {:else if !selectedVariant?.available}
              Sold out
            {:else}
              Add to cart
            {/if}
          </button>
        </div>
      </div>
    </div>
  </div>
{/if}
